import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import EventCard from '../components/EventCard';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

/**
 * LandingPage Component
 * 
 * The main landing page for the YouthXConnect Events portal.
 * Displays upcoming events in a responsive grid with animations.
 * Styled to match the main YouthXConnect site design.
 * 
 * @returns {JSX.Element} - Rendered landing page component
 */
function LandingPage() {
  // State management for events and loading state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch events from Firestore on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsCollection = collection(db, 'events');
        const eventSnapshot = await getDocs(eventsCollection);
        const eventList = eventSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Sort events by date (most recent first)
        eventList.sort((a, b) => {
          const dateA = a.eventDate?.toDate?.() || new Date(a.eventDate || 0);
          const dateB = b.eventDate?.toDate?.() || new Date(b.eventDate || 0);
          return dateA - dateB;
        });
        
        setEvents(eventList);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Animation variants for staggered children
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>YouthXConnect Events - Empowering Youth Through Engaging Events</title>
        <meta name="description" content="Discover and register for upcoming YouthXConnect events. Join our community of youth leaders and innovators." />
      </Helmet>

      
      <motion.div 
        className="container-custom section-padding"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Upcoming Events
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join our community events designed to empower youth through leadership, innovation, and social impact.
          </p>
        </motion.div>

        {/* Events Section */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
          </div>
        ) : events.length > 0 ? (
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {events.map(event => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <EventCard event={event} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="text-xl text-gray-600">No upcoming events at the moment. Check back soon!</p>
          </motion.div>
        )}
      </motion.div>
    </>
  );
}

export default LandingPage;
